import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  IconButton,
  Grid,
  Divider,
  Stack,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ClearIcon from '@mui/icons-material/Clear';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
// Import icons for email providers
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import GoogleIcon from '@mui/icons-material/Google';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from './../utils/logger.js';

const SearchFilters = ({ columns = [], columnTypes = {}, uniqueValues = {}, onSearch = () => {} }) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState({ field: '', direction: 'asc' });
  const [globalSearch, setGlobalSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  
  const [newFilter, setNewFilter] = useState({
    field: '',
    operator: '',
    value: ''
  });

  // Add state for dynamic loading of values
  const [availableValuesList, setAvailableValuesList] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [valuesFetched, setValuesFetched] = useState({});
  
  // Add state for outreach status filtering
  const [outreachStatusFilter, setOutreachStatusFilter] = useState([]);
  
  // Add state for email provider filtering
  const [emailProviderFilter, setEmailProviderFilter] = useState([]);
  
  // Add state for SPF provider filtering
  const [spfProviderFilter, setSpfProviderFilter] = useState([]);
  const [availableSpfProviders, setAvailableSpfProviders] = useState([]);
  const [loadingSpfProviders, setLoadingSpfProviders] = useState(false);
  
  // Add ref for debounce timer
  const fetchTimerRef = useRef(null);

  // Fetch available SPF providers on component mount
  useEffect(() => {
    const fetchSpfProviders = async () => {
      setLoadingSpfProviders(true);
      try {
        const response = await api.getDnsSpfProviderStats();
        if (response && response.success && response.data) {
          // Get top 20 SPF providers for the filter buttons
          const topProviders = response.data.slice(0, 20).map(p => p.provider);
          // Store all providers for the dropdown
          setAvailableSpfProviders(response.data.map(p => p.provider));
          logger.log(`Loaded ${response.data.length} service providers for filter (showing top ${topProviders.length} as buttons)`);
        }
      } catch (error) {
        logger.error('Error fetching service providers:', error);
      } finally {
        setLoadingSpfProviders(false);
      }
    };
    
    fetchSpfProviders();
  }, []);

  // Get operator options based on field type (was column type)
  const getOperatorOptions = (field) => {
    // Special case for the 'claimed' field
    if (field === 'claimed') {
        return [
            { label: 'equals', value: 'equals' },
            { label: 'not equals', value: 'notEquals' }
        ];
    }

    // Special case for claimed_by field
    if (field === 'claimed_by') {
        return [
            { label: 'equals', value: 'equals' },
            { label: 'not equals', value: 'notEquals' },
            { label: 'contains', value: 'contains' }
        ];
    }

    // For numeric columns
    if (columnTypes[field] === 'number') {
        return [
            { label: 'equals', value: 'equals' },
            { label: 'not equals', value: 'notEquals' },
            { label: 'greater than', value: 'greaterThan' },
            { label: 'less than', value: 'lessThan' },
            { label: 'greater than or equal', value: 'greaterThanOrEqual' },
            { label: 'less than or equal', value: 'lessThanOrEqual' }
        ];
    }

    // Default for text columns
    return [
        { label: 'equals', value: 'equals' },
        { label: 'not equals', value: 'notEquals' },
        { label: 'contains', value: 'contains' },
        { label: 'not contains', value: 'notContains' },
        { label: 'starts with', value: 'startsWith' },
        { label: 'ends with', value: 'endsWith' }
    ];
  };

  // Pre-compute operator options to avoid issues in the render
  const operatorOptions = newFilter.field 
    ? getOperatorOptions(newFilter.field) 
    : [];

  // Check if we have a list of unique values for the selected field
  // Only consider it "has values" if the array exists AND has actual values
  const hasUniqueValues = newFilter.field && uniqueValues && uniqueValues[newFilter.field] && uniqueValues[newFilter.field].length > 0;
  
  // Special handling for claimed field
  const isClaimedField = newFilter.field === 'claimed' || newFilter.field === 'claimed_by';
  
  // When field or operator changes, set up appropriate defaults and reset value
  useEffect(() => {
    if (newFilter.field) {
      // Set default operator based on field type
      const defaultOperator = newFilter.field === 'claimed' ? 'equals' : 
                             newFilter.field === 'claimed_by' ? 'equals' : 
                             columnTypes[newFilter.field] === 'number' ? 'equals' : 'contains';
                             
      setNewFilter(prev => ({
        ...prev,
        operator: prev.operator || defaultOperator
      }));
      
      // Special case for "claimed by me" pseudo-filter
      if (newFilter.field === 'claimed_by_me' && user) {
        setNewFilter({
          field: 'claimed_by',
          operator: 'equals',
          value: user.username
        });
      }
      
      // Clear any existing timer
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
      }
      
      // Fetch column values if not already cached and not a special field
      if (!isClaimedField && !hasUniqueValues && !valuesFetched[newFilter.field]) {
        // Show loading indicator immediately
        setLoadingValues(true);
        
        // Use a timer to debounce the API request - this prevents unnecessary
        // requests when switching fields quickly
        fetchTimerRef.current = setTimeout(() => {
          fetchColumnValues(newFilter.field);
        }, 300); // 300ms delay 
      } else if (isClaimedField && newFilter.field === 'claimed') {
        // For claimed field, we already know the values
        setAvailableValuesList(['Yes', 'No']);
      } else if (hasUniqueValues) {
        // If we already have the values from initial load, use them
        setAvailableValuesList(uniqueValues[newFilter.field]);
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
      }
    };
  }, [newFilter.field, columnTypes, user, uniqueValues, isClaimedField, hasUniqueValues, valuesFetched]);
  
  // Function to fetch column values from the API
  const fetchColumnValues = async (columnName) => {
    if (!columnName || isClaimedField) return;
    
    // Don't reset loading state as it's already set in the effect
    
    try {
      logger.log(`Fetching values for column: ${columnName}`);
      const response = await api.getColumnValues(columnName, 1000); // Increase limit for better autocomplete experience
      
      if (response && response.values) {
        logger.log(`Received ${response.values.length} values for ${columnName}`);
        
        // Store values in state
        setAvailableValuesList(response.values);
        
        // Mark this column as fetched
        setValuesFetched(prev => ({
          ...prev,
          [columnName]: true
        }));
        
        // Log if values were truncated
        if (response.truncated) {
          logger.log(`Values for ${columnName} were truncated - showing ${response.values.length} of ${response.total} total values`);
        }
      } else {
        logger.error('Failed to fetch column values:', response?.message || 'Invalid response format');
        // Set empty array for values instead of keeping previous values
        setAvailableValuesList([]);
      }
    } catch (error) {
      logger.error(`Error fetching values for column ${columnName}:`, error);
      // Set empty array for values to avoid confusion
      setAvailableValuesList([]);
    } finally {
      setLoadingValues(false);
    }
  };
  
  // Get available values for the selected field
  const getAvailableValues = () => {
    if (isClaimedField) {
      if (newFilter.field === 'claimed') {
        return ['Yes', 'No'];
      }
      return [];
    }
    
    // Return the dynamically loaded values or the cached unique values
    return availableValuesList.length > 0 ? 
           availableValuesList : 
           (hasUniqueValues ? uniqueValues[newFilter.field] : []);
  };
  
  // Handle outreach status filter changes
  const handleOutreachStatusChange = (event, newStatuses) => {
    
    // Check for special filter conditions
    const hasNoOutreach = newStatuses.includes('no_outreach');
    const hasOutreachTypes = newStatuses.some(status => 
      ['phone', 'email', 'linkedin', 'meeting', 'followup', 'reached', 'not_reached'].includes(status)
    );
    
    // Check for mutually exclusive filters - reached and not_reached
    const hasReached = newStatuses.includes('reached');
    const hasNotReached = newStatuses.includes('not_reached');
    
    // Prevent conflicting filters (no outreach + has outreach)
    if (hasNoOutreach && hasOutreachTypes) {
      // If no_outreach is newly selected, remove all other filters
      if (!outreachStatusFilter.includes('no_outreach')) {
        newStatuses = ['no_outreach'];
      } else {
        // Otherwise, remove no_outreach if other filters are added
        newStatuses = newStatuses.filter(status => status !== 'no_outreach');
      }
    }
    
    // Prevent conflicting filters (reached + not_reached)
    if (hasReached && hasNotReached) {
      // Keep the most recently selected option
      const wasReachedSelected = outreachStatusFilter.includes('reached');
      const wasNotReachedSelected = outreachStatusFilter.includes('not_reached');
      
      if (wasReachedSelected && !wasNotReachedSelected) {
        // Remove reached, keep not_reached
        newStatuses = newStatuses.filter(status => status !== 'reached');
      } else {
        // Otherwise, remove not_reached, keep reached
        newStatuses = newStatuses.filter(status => status !== 'not_reached');
      }
    }
    
    // Update the selected statuses
    setOutreachStatusFilter(newStatuses);
    
    // Remove any existing outreach status filters
    const filteredFilters = filters.filter(f => 
      !f.field?.startsWith('outreach_status')
    );
    
    // Create new filters based on selected statuses
    const newFilters = [...filteredFilters];
    
    // If nothing is selected, clear all outreach filters and return
    if (newStatuses.length === 0) {
      logger.log("No outreach status selected, clearing all outreach filters");
      setFilters(newFilters);
      
      // Apply the cleared filters
      onSearch({
        filters: newFilters,
        sort: sort.field ? sort : null,
        search: globalSearch
      });
      
      return;
    }
    
    // Handle the special case of no_outreach filter
    if (hasNoOutreach) {
      logger.log("Adding no_outreach filter");
      newFilters.push({
        field: 'outreach_status.has_outreach',
        operator: 'notEquals',
        value: 'true',
        display: 'No outreach activity'
      });
      
      // No need for other filters when filtering for no outreach
      setFilters(newFilters);
      
      // Apply the filters
      onSearch({
        filters: newFilters,
        sort: sort.field ? sort : null,
        search: globalSearch
      });
      
      return;
    }
    
    // A common filter to check if the record has outreach status
    newFilters.push({
      field: 'outreach_status.has_outreach',
      operator: 'equals',
      value: 'true',
      display: 'Has outreach activity'
    });
    
    // Handle reached/not reached status
    if (hasReached) {
      logger.log("Adding reached filter");
      newFilters.push({
        field: 'outreach_status.reached_intended_contact',
        operator: 'equals',
        value: 'true',
        display: 'Contact was reached'
      });
    }
    
    if (hasNotReached) {
      logger.log("Adding not_reached filter");
      newFilters.push({
        field: 'outreach_status.reached_intended_contact',
        operator: 'notEquals',
        value: 'true',
        display: 'Contact was not reached'
      });
    }
    
    // Handle the meeting_scheduled status
    if (newStatuses.includes('meeting')) {
      logger.log("Adding meeting filter");
      newFilters.push({
        field: 'outreach_status.meeting_scheduled',
        operator: 'equals',
        value: 'true',
        display: 'Has scheduled meeting'
      });
    }
    
    // Handle the followup_date status
    if (newStatuses.includes('followup')) {
      logger.log("Adding followup filter");
      newFilters.push({
        field: 'outreach_status.followup_date',
        operator: 'notEquals',
        value: '',  // Non-empty value check
        display: 'Has follow-up scheduled'
      });
    }
    
    // Add method-specific filters
    const methods = ['phone', 'email', 'linkedin'].filter(method => newStatuses.includes(method));
    
    if (methods.length > 0) {
      // If multiple methods are selected, use the 'in' operator for OR logic
      if (methods.length > 1) {
        newFilters.push({
          field: 'outreach_status.outreach_method',
          operator: 'in',
          value: methods,
          display: `Outreach methods: ${methods.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(' or ')}`
        });
      } else {
        // Single method is straightforward
        newFilters.push({
          field: 'outreach_status.outreach_method',
          operator: 'equals',
          value: methods[0],
          display: `${methods[0].charAt(0).toUpperCase() + methods[0].slice(1)} outreach`
        });
      }
    }
    
    
    // Update state
    setFilters(newFilters);
    
    // Apply the filters
    onSearch({
      filters: newFilters,
      sort: sort.field ? sort : null,
      search: globalSearch
    });
  };
  
  // Handle email provider filter changes
  const handleEmailProviderChange = (event, newProviders) => {
    logger.log("Email provider filter changed:", newProviders);
    
    // Update the selected providers
    setEmailProviderFilter(newProviders);
    
    // Remove any existing email provider filters
    const filteredFilters = filters.filter(f => 
      !f.field?.startsWith('email_provider')
    );
    
    // Create new filters based on selected providers
    const newFilters = [...filteredFilters];
    
    // If nothing is selected, clear all email provider filters and return
    if (newProviders.length === 0) {
      logger.log("No email provider selected, clearing email provider filters");
      setFilters(newFilters);
      
      // Apply the cleared filters
      onSearch({
        filters: newFilters,
        sort: sort.field ? sort : null,
        search: globalSearch
      });
      
      return;
    }
    
    // If multiple providers are selected, use the 'in' operator for OR logic
    if (newProviders.length > 1) {
      logger.log("Adding multiple email provider filters:", newProviders);
      newFilters.push({
        field: 'email_provider',
        operator: 'in',
        value: newProviders,
        display: `Email providers: ${newProviders.join(' or ')}`
      });
    } else {
      // Single provider filter
      const provider = newProviders[0];
      logger.log("Adding single email provider filter:", provider);
      
      let displayName = provider;
      if (provider === 'M365') displayName = 'Microsoft 365';
      if (provider === 'M365_Filtered') displayName = 'Microsoft 365 (Filtered)';
      
      newFilters.push({
        field: 'email_provider',
        operator: 'equals',
        value: provider,
        display: `Email provider: ${displayName}`
      });
    }
    
    // Update state
    setFilters(newFilters);
    
    // Apply the filters
    onSearch({
      filters: newFilters,
      sort: sort.field ? sort : null,
      search: globalSearch
    });
  };
  
  // Handle SPF provider filter changes
  const handleSpfProviderChange = (event, newProviders) => {
    logger.log("Service provider filter changed:", newProviders);
    
    // Update the selected providers
    setSpfProviderFilter(newProviders);
    
    // Remove any existing SPF provider filters
    const filteredFilters = filters.filter(f => 
      !f.field?.startsWith('spf_provider')
    );
    
    // Create new filters based on selected providers
    const newFilters = [...filteredFilters];
    
    // If nothing is selected, clear all SPF provider filters and return
    if (newProviders.length === 0) {
      logger.log("No service provider selected, clearing service provider filters");
      setFilters(newFilters);
      
      // Apply the cleared filters
      onSearch({
        filters: newFilters,
        sort: sort.field ? sort : null,
        search: globalSearch
      });
      
      return;
    }
    
    // If multiple providers are selected, use the 'in' operator for OR logic
    if (newProviders.length > 1) {
      logger.log("Adding multiple service provider filters:", newProviders);
      newFilters.push({
        field: 'spf_provider',
        operator: 'in',
        value: newProviders,
        display: `Service providers: ${newProviders.join(' or ')}`
      });
    } else {
      // Single provider filter
      const provider = newProviders[0];
      logger.log("Adding single service provider filter:", provider);
      
      newFilters.push({
        field: 'spf_provider',
        operator: 'equals',
        value: provider,
        display: `Service provider: ${provider}`
      });
    }
    
    // Update state
    setFilters(newFilters);
    
    // Apply the filters
    onSearch({
      filters: newFilters,
      sort: sort.field ? sort : null,
      search: globalSearch
    });
  };
  
  const handleAddFilter = (preset = null) => {
    let newFilterObj;
    
    if (preset === 'claimed_by_me' && user) {
      newFilterObj = {
        field: 'claimed_by',
        operator: 'equals',
        value: user.username
      };
    } else {
      const isBooleanField = newFilter.field === 'claimed';
      
      // Some basic validation
      if (!newFilter.field) return;
      if (!isBooleanField && !newFilter.value) return;
      
      newFilterObj = { ...newFilter };
    }
    
    // Skip if there's already an identical filter
    if (filters.some(f => 
      f.field === newFilterObj.field && 
      f.operator === newFilterObj.operator && 
      f.value === newFilterObj.value
    )) {
      logger.log("Filter already exists, skipping...");
      return;
    }
    
    logger.log("Adding new filter:", newFilterObj);
    setFilters([...filters, newFilterObj]);
    
    // Reset the filter form
    setNewFilter({ 
      field: '', 
      operator: '', 
      value: '' 
    });
  };
  
  const handleRemoveFilter = (index) => {
    const updatedFilters = [...filters];
    
    // Check if we're removing an outreach status filter and update the toggle buttons
    const removedFilter = updatedFilters[index];
    if (removedFilter) {
      if (removedFilter.field === 'outreach_status.meeting_scheduled') {
        setOutreachStatusFilter(prev => prev.filter(status => status !== 'meeting'));
      } else if (removedFilter.field === 'outreach_status.has_followup') {
        setOutreachStatusFilter(prev => prev.filter(status => status !== 'followup'));
      } else if (removedFilter.field === 'outreach_status.outreach_method') {
        // Remove all method buttons
        if (Array.isArray(removedFilter.value)) {
          setOutreachStatusFilter(prev => prev.filter(status => 
            !['phone', 'email', 'linkedin'].includes(status)));
        } else {
          setOutreachStatusFilter(prev => prev.filter(status => status !== removedFilter.value));
        }
      } else if (removedFilter.field === 'email_provider') {
        // Remove email provider filter buttons
        if (Array.isArray(removedFilter.value)) {
          setEmailProviderFilter([]);
        } else {
          setEmailProviderFilter(prev => prev.filter(provider => provider !== removedFilter.value));
        }
      } else if (removedFilter.field === 'spf_provider') {
        // Remove SPF provider filter buttons
        if (Array.isArray(removedFilter.value)) {
          setSpfProviderFilter([]);
        } else {
          setSpfProviderFilter(prev => prev.filter(provider => provider !== removedFilter.value));
        }
      }
    }
    
    updatedFilters.splice(index, 1);
    setFilters(updatedFilters);
  };
  
  const handleApplyFilters = () => {
    let payload = {
      filters: [...filters],
      sortField: sort.field,
      sortOrder: sort.direction,
      search: globalSearch,
      page: 1
    };

    // Add outreach status filters if any are selected
    if (outreachStatusFilter.length > 0) {
      outreachStatusFilter.forEach(method => {
        payload.filters.push({
          field: 'outreach_status.outreach_method',
          operator: 'equals',
          value: method,
          display: `Outreach method: ${method}`
        });
      });
    }

    // Add email provider filters if any are selected
    if (emailProviderFilter.length > 0) {
      emailProviderFilter.forEach(provider => {
        payload.filters.push({
          field: 'email_provider',
          operator: 'equals',
          value: provider,
          display: `Email provider: ${provider === 'M365' ? 'Microsoft 365' : provider}`
        });
      });
    }

    onSearch(payload);
    setExpanded(false);
  };
  
  const handleClearFilters = () => {
    setFilters([]);
    setGlobalSearch('');
    setSort({ field: '', direction: 'asc' });
    setOutreachStatusFilter([]);
    setEmailProviderFilter([]);
    setSpfProviderFilter([]);
    
    onSearch({ filters: [], sort: null, search: '' });
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  // Get available values for the current filter
  const availableValues = getAvailableValues();
  
  return (
    <Box sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <FilterListIcon sx={{ mr: 1 }} /> Search & Filters
        </Typography>
        <IconButton onClick={toggleExpanded} size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      {/* Global Search Field - Always visible */}
      <Box sx={{ mb: expanded ? 3 : 1 }}>
        <TextField
          fullWidth
          label="Search in all fields"
          placeholder="Type to search across all data..."
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleApplyFilters();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          helperText="Press Enter to search or use the Apply button below"
          size="small"
        />
      </Box>
      
      {/* Outreach Status Filters - Always visible */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Outreach Status Filter
        </Typography>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 1, 
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2
          }}
        >
          <ToggleButtonGroup
            value={outreachStatusFilter}
            onChange={handleOutreachStatusChange}
            aria-label="outreach status"
            size="small"
            multiple
          >
            <ToggleButton value="no_outreach" aria-label="no outreach status">
              <Tooltip title="No Outreach Activity">
                <BlockIcon color="error" />
              </Tooltip>
            </ToggleButton>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <ToggleButton value="phone" aria-label="phone outreach">
              <Tooltip title="Phone Outreach">
                <PhoneIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="email" aria-label="email outreach">
              <Tooltip title="Email Outreach">
                <EmailIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="linkedin" aria-label="linkedin outreach">
              <Tooltip title="LinkedIn Outreach">
                <LinkedInIcon />
              </Tooltip>
            </ToggleButton>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <ToggleButton value="reached" aria-label="contact reached">
              <Tooltip title="Contact Reached">
                <CheckCircleIcon color="success" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="not_reached" aria-label="contact not reached">
              <Tooltip title="Contact Not Reached">
                <CancelIcon color="warning" />
              </Tooltip>
            </ToggleButton>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <ToggleButton value="meeting" aria-label="has meeting">
              <Tooltip title="Has Scheduled Meeting">
                <EventIcon color="success" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="followup" aria-label="has followup">
              <Tooltip title="Has Follow-up">
                <ScheduleIcon color="info" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          {outreachStatusFilter.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {outreachStatusFilter.length} active
              </Typography>
              <Tooltip title="Clear outreach filters">
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => {
                    // Clear outreach status filter
                    setOutreachStatusFilter([]);
                    
                    // Remove outreach-related filters
                    const updatedFilters = filters.filter(f => 
                      !f.field?.startsWith('outreach_status')
                    );
                    
                    // Update filters
                    setFilters(updatedFilters);
                    
                    // Apply the change
                    onSearch({
                      filters: updatedFilters,
                      sort: sort.field ? sort : null,
                      search: globalSearch
                    });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* Email Provider Filters - Always visible */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Email Provider Filter
        </Typography>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 1, 
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2
          }}
        >
          <ToggleButtonGroup
            value={emailProviderFilter}
            onChange={handleEmailProviderChange}
            aria-label="email provider"
            size="small"
            multiple
          >
            <ToggleButton value="M365" aria-label="microsoft 365">
              <Tooltip title="Microsoft 365">
                <MicrosoftIcon color="primary" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="M365_Filtered" aria-label="microsoft 365 filtered">
              <Tooltip title="Microsoft 365 (Filtered)">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MicrosoftIcon color="primary" fontSize="small" />
                  <FilterAltIcon color="warning" fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <ToggleButton value="Google" aria-label="google workspace">
              <Tooltip title="Google Workspace">
                <GoogleIcon color="error" />
              </Tooltip>
            </ToggleButton>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <ToggleButton value="Other" aria-label="other provider">
              <Tooltip title="Other Email Provider">
                <AlternateEmailIcon color="info" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="Unknown" aria-label="unknown provider">
              <Tooltip title="Unknown Email Provider">
                <QuestionMarkIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          {emailProviderFilter.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {emailProviderFilter.length} active
              </Typography>
              <Tooltip title="Clear email provider filters">
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => {
                    // Clear email provider filter
                    setEmailProviderFilter([]);
                    
                    // Remove email provider-related filters
                    const updatedFilters = filters.filter(f => 
                      !f.field?.startsWith('email_provider')
                    );
                    
                    // Update filters
                    setFilters(updatedFilters);
                    
                    // Apply the change
                    onSearch({
                      filters: updatedFilters,
                      sort: sort.field ? sort : null,
                      search: globalSearch
                    });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* Service Provider Filters - Always visible */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Service Provider Filter
        </Typography>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 1, 
            bgcolor: 'background.default',
            minHeight: '48px'
          }}
        >
          {loadingSpfProviders ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading service providers...
              </Typography>
            </Box>
          ) : availableSpfProviders.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Top 20 providers as toggle buttons */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: 2
              }}>
                <ToggleButtonGroup
                  value={spfProviderFilter}
                  onChange={handleSpfProviderChange}
                  aria-label="service provider"
                  size="small"
                  multiple
                  sx={{ flexWrap: 'wrap' }}
                >
                  {availableSpfProviders.slice(0, 20).map((provider, index) => (
                    <ToggleButton key={provider} value={provider} aria-label={provider}>
                      <Tooltip title={`Filter by ${provider}`}>
                        <Typography variant="caption" sx={{ px: 0.5 }}>
                          {provider}
                        </Typography>
                      </Tooltip>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                
                {spfProviderFilter.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spfProviderFilter.length} active
                    </Typography>
                    <Tooltip title="Clear service provider filters">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => {
                          // Clear SPF provider filter
                          setSpfProviderFilter([]);
                          
                          // Remove SPF provider-related filters
                          const updatedFilters = filters.filter(f => 
                            !f.field?.startsWith('spf_provider')
                          );
                          
                          // Update filters
                          setFilters(updatedFilters);
                          
                          // Apply the change
                          onSearch({
                            filters: updatedFilters,
                            sort: sort.field ? sort : null,
                            search: globalSearch
                          });
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              
              {/* Dropdown for selecting additional providers */}
              {availableSpfProviders.length > 20 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Or select from all providers:
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableSpfProviders}
                    value={spfProviderFilter}
                    onChange={(event, newValue) => {
                      handleSpfProviderChange(event, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Service Providers"
                        placeholder="Type to search..."
                      />
                    )}
                    sx={{ minWidth: 300, flexGrow: 1 }}
                    filterSelectedOptions
                    disableCloseOnSelect
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No service providers available
            </Typography>
          )}
        </Paper>
      </Box>
      
      {/* Expanded filter options */}
      {expanded && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Filter Builder Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Add Filter</Typography>
            
            <Grid container spacing={2} alignItems="flex-start">
              {/* Field Selection */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-field-label">Field</InputLabel>
                  <Select
                    labelId="filter-field-label"
                    value={newFilter.field}
                    label="Field"
                    onChange={(e) => setNewFilter({
                      ...newFilter,
                      field: e.target.value,
                      operator: '',
                      value: ''
                    })}
                  >
                    <MenuItem value="">Select a field</MenuItem>
                    {/* Add special options for claimed status */}
                    <MenuItem value="claimed" divider>
                      <em>Claimed Status</em>
                    </MenuItem>
                    {user && (
                      <MenuItem value="claimed_by_me">
                        <em>Claimed by me</em>
                      </MenuItem>
                    )}
                    <MenuItem value="claimed_by" divider>
                      <em>Claimed by User</em>
                    </MenuItem>
                    {columns && columns.map((column, index) => (
                      column !== 'claimed' && column !== 'claimed_by' && (
                        <MenuItem key={column || `column-${index}`} value={column}>
                          {column}
                        </MenuItem>
                      )
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={newFilter.operator}
                    label="Operator"
                    onChange={(e) => setNewFilter({ ...newFilter, operator: e.target.value })}
                    disabled={!newFilter.field || newFilter.field === 'claimed_by_me'}
                  >
                    {newFilter.field && operatorOptions.length > 0 ? (
                      operatorOptions.map((op) => (
                        <MenuItem 
                          key={op.value} 
                          value={op.value}
                          sx={op.value === 'exclude' ? { color: 'error.main' } : {}}
                        >
                          {op.value === 'exclude' ? `⊘ ${op.label}` : op.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="">
                        <em>Select a field first</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                {newFilter.field === 'claimed_by_me' ? (
                  <TextField
                    fullWidth
                    label="Value"
                    size="small"
                    value={user?.username || ''}
                    disabled
                  />
                ) : (hasUniqueValues || isClaimedField || availableValuesList.length > 0) && availableValues.length > 0 ? (
                  <Autocomplete
                    size="small"
                    options={availableValues}
                    value={newFilter.value}
                    onChange={(e, newValue) => setNewFilter({ ...newFilter, value: newValue || '' })}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Value" 
                        fullWidth 
                        disabled={!newFilter.field || newFilter.field === 'claimed_by_me'}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingValues ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    loading={loadingValues}
                    disabled={!newFilter.field || newFilter.field === 'claimed_by_me'}
                    openOnFocus
                    filterOptions={(options, state) => {
                      // Show all options when focused without typing
                      if (!state.inputValue) return options;
                      
                      // Case-insensitive filtering
                      const inputValueLower = state.inputValue.toLowerCase();
                      return options.filter(option => 
                        String(option).toLowerCase().includes(inputValueLower)
                      );
                    }}
                    autoHighlight
                    disableCloseOnSelect={false}
                    blurOnSelect={false}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Value"
                    size="small"
                    value={newFilter.value}
                    onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                    disabled={!newFilter.field || newFilter.field === 'claimed_by_me'}
                    type={columnTypes && columnTypes[newFilter.field] === 'numeric' ? 'number' : 'text'}
                  />
                )}
              </Grid>
              
              <Grid item xs={12} sm={1}>
                <IconButton 
                  color="primary" 
                  onClick={() => handleAddFilter(newFilter.field === 'claimed_by_me' ? 'claimed_by_me' : null)}
                  disabled={
                    !newFilter.field || 
                    (newFilter.field !== 'claimed_by_me' && !newFilter.value)
                  }
                >
                  <AddIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
          
          {filters && filters.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Active Filters</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {filters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={filter.display || (filter.field === 'claimed_by' && filter.value === user?.username 
                      ? 'Claimed by me' 
                      : `${filter.field || ''} ${filter.operator || ''} "${Array.isArray(filter.value) ? filter.value.join(', ') : filter.value || ''}"`)}
                    onDelete={() => handleRemoveFilter(index)}
                    color={filter.operator === 'exclude' ? 'error' : 'primary'}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <SortIcon sx={{ mr: 1, fontSize: 20 }} /> Sort By
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Field</InputLabel>
                  <Select
                    value={sort.field}
                    label="Field"
                    onChange={(e) => setSort({ ...sort, field: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {columns && columns.map((column, index) => (
                      <MenuItem key={column || `column-sort-${index}`} value={column}>
                        {column}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!sort.field}>
                  <InputLabel>Direction</InputLabel>
                  <Select
                    value={sort.direction || 'asc'}
                    label="Direction"
                    onChange={(e) => setSort({ ...sort, direction: e.target.value })}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: expanded ? 3 : 2 }}>
        {expanded ? (
          <>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={handleClearFilters}
              disabled={filters.length === 0 && !sort.field && !globalSearch && outreachStatusFilter.length === 0 && emailProviderFilter.length === 0 && spfProviderFilter.length === 0}
            >
              Clear All
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>
          </>
        ) : (
          <>
            <Button 
              variant="outlined"
              color="error"
              onClick={handleClearFilters}
              disabled={!globalSearch && outreachStatusFilter.length === 0 && emailProviderFilter.length === 0 && spfProviderFilter.length === 0}
              sx={{ visibility: globalSearch || outreachStatusFilter.length > 0 || emailProviderFilter.length > 0 || spfProviderFilter.length > 0 ? 'visible' : 'hidden' }}
            >
              Clear
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleApplyFilters}
            >
              Search
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchFilters; 